/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Button, Checkbox, Group, Loader, Select, Space, Stack, Text } from '@mantine/core';
import { i18n } from '@lingui/core';
import { FC, useEffect, useState } from 'react';
import { IconBallpen, IconTemplate } from '@tabler/icons';
import { closeAllModals } from '@mantine/modals';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { AgencyTemplateDTO, EmailContent } from '../../../../Types/MessageT';
import { BaseInput, RichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { DataS } from '../../../../Service/DataS';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { TiptapS } from '../../../../Service/TiptapS';
import { LicenceRestriction, LicenceS } from '../../../../Service/LicenceS';
import AttachmentsAlert from './AttachmentsAlert';

interface Props {
  removeAllRecommendations: () => void;
  eventId: number;
  executeWithLicenceCheck: (licenceRestriction: LicenceRestriction, featureHandler: () => unknown) => void;
}

const RecommendationsDeleteModal: FC<Props> = ({ removeAllRecommendations, eventId, executeWithLicenceCheck }) => {
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customSubject, setCustomSubject] = useState(false);
  const { templates } = useAgencyTemplates();
  const { profileId } = useJwtClaims();
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const [showAttachmentsAlert, setShowAttachmentsAlert] = useState(false);

  const [mail, setMail] = useState<EmailContent>({ subject: '', message: '' });
  const { subject, message } = mail;

  const updateProp = (patch: Partial<EmailContent>) => setMail((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    if (selectedTemplate) {
      const handler = () => {
        setMail(selectedTemplate);
        if (selectedTemplate.attachments.length > 0) {
          setShowAttachmentsAlert(true);
        } else {
          setShowAttachmentsAlert(false);
        }
        if (!customSubject) {
          setCustomSubject(true);
        }
      };
      executeWithLicenceCheck(LicenceS.Restrictions.AGENCY_TEMPLATES, handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const onSubmit = async () => {
    setLoading(true);
    const placeholdersResolved = await PlaceholderS.checkPlaceholders({
      profileId,
      eventId: Number(eventId),
      message,
      subject,
    });
    if (placeholdersResolved) {
      const deleted = await RecommendationS.removeEventRecommendations(eventId, sendEmail, {
        message,
        subject,
        newVersion: true,
      });

      if (deleted) {
        removeAllRecommendations();
        closeAllModals();
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Text mb="xl">{i18n._('event.recommendations.delete.confirm')}</Text>
      <Text>Soll der Dienstleister darüber per E-Mail benachrichtigt werden?</Text>
      <Checkbox
        size="xs"
        my="md"
        label="Ja, Dienstleister per E-Mail benachrichtigen"
        disabled={loading}
        checked={sendEmail}
        onChange={() => setSendEmail(!sendEmail)}
      />
      {sendEmail && (
        <>
          {templates.length > 0 && (
            <Select
              mb="xs"
              label={
                <Text size="xs" color="dimmed">
                  Vorlage
                </Text>
              }
              icon={<IconTemplate size={20} />}
              data={DataS.getAgencyTemplateOptions(templates)}
              value={selectedTemplate ? selectedTemplate.id : undefined}
              onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
            />
          )}
          {showAttachmentsAlert && <AttachmentsAlert />}
          {!customSubject && (
            <Group position="apart">
              <Stack spacing={0}>
                <Text mb={3} size={12} color="dimmed">
                  Bereff
                </Text>
                <Text>
                  Vermittlung entfernt für Event am <span className="mention">Vermittlungsdatum</span>
                </Text>
              </Stack>
              <ActionIcon>
                <IconBallpen size={20} onClick={() => setCustomSubject(true)} />
              </ActionIcon>
            </Group>
          )}
          {customSubject && (
            <BaseInput
              withUpdate
              toolbar={<BaseToolbar />}
              label="Bereff"
              value={subject}
              onUpdate={(html) => updateProp({ subject: html })}
            />
          )}
          <Space h="sm" />
          <Text mb="sm">
            Hey <span className="mention">Dienstleister-Name</span>,
          </Text>
          <Text mb="sm">
            die Anfrage für das Event am <span className="mention">Vermittlungsdatum</span> hat sich erledigt und Du
            kannst den Termin wieder freigeben. Trotzdem Danke für's freihalten!
          </Text>
          <RichInput
            withUpdate
            styles={TiptapS.TextAreaStyles}
            label="Nachricht an den Dienstleister"
            value={message}
            onUpdate={(html) => updateProp({ message: html })}
            toolbar={
              <TemplateMessageToolbar
                saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments: [] } })}
              />
            }
          />
          <Text mt="xs" size="xs" color="dimmed">
            Hinweis: Diese Nachricht wird zur Zeit noch nicht im System gespeichert - Du kannst Sie also im nicht noch
            einmal einsehen.
          </Text>
        </>
      )}
      <Group position="right" mt="xl">
        <Button color="gray" variant="subtle" onClick={() => closeAllModals()}>
          {i18n._('actions.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          color="red"
          variant="subtle"
          sx={{ backgroundColor: '#FFEFEF' }}
          type="submit"
          disabled={loading}
          leftIcon={loading && <Loader size="xs" />}
        >
          {i18n._('Vermittlung löschen')}
        </Button>
      </Group>
    </>
  );
};

export default RecommendationsDeleteModal;
